import React, { useState } from 'react';
import { Sponsor, SponsorCategory, SponsorTier, SponsorPlacementLocation, SportType } from '../../types';
import { triggerHaptic } from '../../utils/haptics';
import { 
  Megaphone, 
  Plus, 
  Search, 
  Filter, 
  Sparkles, 
  Edit3, 
  Trash2, 
  Eye, 
  EyeOff, 
  Tag, 
  ExternalLink, 
  Copy, 
  Check, 
  BarChart3, 
  MousePointerClick, 
  Award, 
  Building2, 
  RefreshCw, 
  Layers, 
  ChevronRight, 
  X, 
  CheckCircle2, 
  AlertCircle,
  HelpCircle
} from 'lucide-react';

interface SponsorManagementTabProps {
  sponsors: Sponsor[];
  onAddSponsor: (sponsor: Sponsor) => void;
  onUpdateSponsor: (sponsorId: string, updates: Partial<Sponsor>) => void;
  onDeleteSponsor: (sponsorId: string) => void;
  onResetSponsors?: () => void;
}

const PRESET_LOGOS = [
  { name: 'Nike', url: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=150&auto=format&fit=crop&q=80' },
  { name: 'Gatorade', url: 'https://images.unsplash.com/photo-1622483767028-3f66f32aef97?w=150&auto=format&fit=crop&q=80' },
  { name: 'D1 Scout', url: 'https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=150&auto=format&fit=crop&q=80' },
  { name: 'Wilson', url: 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=150&auto=format&fit=crop&q=80' },
  { name: 'Hyperice', url: 'https://images.unsplash.com/photo-1574680096145-d05b474e2155?w=150&auto=format&fit=crop&q=80' },
  { name: 'Red Bull', url: 'https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=150&auto=format&fit=crop&q=80' },
];

export const SponsorManagementTab: React.FC<SponsorManagementTabProps> = ({
  sponsors,
  onAddSponsor,
  onUpdateSponsor,
  onDeleteSponsor,
  onResetSponsors,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [placementFilter, setPlacementFilter] = useState<string>('all');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);

  // Form State
  const [formBrandName, setFormBrandName] = useState('');
  const [formCompanyName, setFormCompanyName] = useState('');
  const [formLogoUrl, setFormLogoUrl] = useState('');
  const [formBannerUrl, setFormBannerUrl] = useState('');
  const [formCategory, setFormCategory] = useState<SponsorCategory>('gear');
  const [formTier, setFormTier] = useState<SponsorTier>('gold');
  const [formTargetSports, setFormTargetSports] = useState<(SportType | 'all')[]>(['all']);
  const [formPlacementLocations, setFormPlacementLocations] = useState<SponsorPlacementLocation[]>(['dashboard', 'tournaments']);
  const [formHeadline, setFormHeadline] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formCtaText, setFormCtaText] = useState('Claim Offer');
  const [formCtaLink, setFormCtaLink] = useState('https://');
  const [formDiscountCode, setFormDiscountCode] = useState('');
  const [formXpReward, setFormXpReward] = useState<number>(25);
  const [formStatus, setFormStatus] = useState<'active' | 'paused' | 'draft'>('active');
  const [formContactEmail, setFormContactEmail] = useState('');
  const [formNotes, setFormNotes] = useState('');

  // Delete Confirm Modal State
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Filter sponsors list
  const filteredSponsors = sponsors.filter((s) => {
    if (statusFilter !== 'all' && s.status !== statusFilter) return false;
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (placementFilter !== 'all' && !s.placementLocations.includes(placementFilter as SponsorPlacementLocation)) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchBrand = s.brandName.toLowerCase().includes(q);
      const matchCompany = s.companyName.toLowerCase().includes(q);
      const matchHeadline = s.headline.toLowerCase().includes(q);
      const matchCode = s.discountCode?.toLowerCase().includes(q);
      if (!matchBrand && !matchCompany && !matchHeadline && !matchCode) return false;
    }
    return true;
  });

  // Calculate Metrics
  const totalActive = sponsors.filter((s) => s.status === 'active').length;
  const totalImpressions = sponsors.reduce((acc, s) => acc + (s.impressionCount || 0), 0);
  const totalClicks = sponsors.reduce((acc, s) => acc + (s.clickCount || 0), 0);
  const avgCtr = totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
  const totalXpClaimed = sponsors.reduce((acc, s) => acc + ((s.clickCount || 0) * (s.xpReward || 0)), 0);

  const openCreateModal = () => {
    triggerHaptic('light');
    setEditingSponsor(null);
    setFormBrandName('');
    setFormCompanyName('');
    setFormLogoUrl(PRESET_LOGOS[0].url);
    setFormBannerUrl('');
    setFormCategory('gear');
    setFormTier('gold');
    setFormTargetSports(['all']);
    setFormPlacementLocations(['dashboard', 'tournaments']);
    setFormHeadline('Official Sports Industry Partner Offer');
    setFormDescription('Exclusive equipment perks & discounts for verified Playground League athletes.');
    setFormCtaText('Claim Player Perk');
    setFormCtaLink('https://example.com');
    setFormDiscountCode('PLAYGROUND20');
    setFormXpReward(25);
    setFormStatus('active');
    setFormContactEmail('partners@sponsor.com');
    setFormNotes('Loaded via Admin Sponsor Management Control Panel.');
    setIsModalOpen(true);
  };

  const openEditModal = (sponsor: Sponsor) => {
    triggerHaptic('light');
    setEditingSponsor(sponsor);
    setFormBrandName(sponsor.brandName);
    setFormCompanyName(sponsor.companyName);
    setFormLogoUrl(sponsor.logoUrl);
    setFormBannerUrl(sponsor.bannerUrl || '');
    setFormCategory(sponsor.category);
    setFormTier(sponsor.sponsorTier);
    setFormTargetSports(sponsor.targetSports || ['all']);
    setFormPlacementLocations(sponsor.placementLocations || ['dashboard']);
    setFormHeadline(sponsor.headline);
    setFormDescription(sponsor.description);
    setFormCtaText(sponsor.ctaText);
    setFormCtaLink(sponsor.ctaLink);
    setFormDiscountCode(sponsor.discountCode || '');
    setFormXpReward(sponsor.xpReward || 20);
    setFormStatus(sponsor.status);
    setFormContactEmail(sponsor.contactEmail || '');
    setFormNotes(sponsor.notes || '');
    setIsModalOpen(true);
  };

  const handleSaveSponsor = (e: React.FormEvent) => {
    e.preventDefault();
    triggerHaptic('medium');

    if (!formBrandName.trim() || !formHeadline.trim()) {
      return;
    }

    if (editingSponsor) {
      onUpdateSponsor(editingSponsor.id, {
        brandName: formBrandName,
        companyName: formCompanyName || formBrandName,
        logoUrl: formLogoUrl || PRESET_LOGOS[0].url,
        bannerUrl: formBannerUrl,
        category: formCategory,
        sponsorTier: formTier,
        targetSports: formTargetSports,
        placementLocations: formPlacementLocations,
        headline: formHeadline,
        description: formDescription,
        ctaText: formCtaText,
        ctaLink: formCtaLink,
        discountCode: formDiscountCode,
        xpReward: formXpReward,
        status: formStatus,
        contactEmail: formContactEmail,
        notes: formNotes,
      });
    } else {
      const newSponsor: Sponsor = {
        id: `spon_custom_${Date.now()}`,
        brandName: formBrandName,
        companyName: formCompanyName || formBrandName,
        logoUrl: formLogoUrl || PRESET_LOGOS[0].url,
        bannerUrl: formBannerUrl,
        category: formCategory,
        sponsorTier: formTier,
        targetSports: formTargetSports,
        placementLocations: formPlacementLocations,
        headline: formHeadline,
        description: formDescription,
        ctaText: formCtaText,
        ctaLink: formCtaLink,
        discountCode: formDiscountCode,
        xpReward: formXpReward,
        status: formStatus,
        impressionCount: 0,
        clickCount: 0,
        createdAt: new Date().toISOString(),
        contactEmail: formContactEmail,
        notes: formNotes,
      };
      onAddSponsor(newSponsor);
    }

    setIsModalOpen(false);
  };

  const toggleSportInForm = (sport: SportType | 'all') => {
    if (sport === 'all') {
      setFormTargetSports(['all']);
      return;
    }
    const current = formTargetSports.filter((s) => s !== 'all');
    if (current.includes(sport)) {
      const updated = current.filter((s) => s !== sport);
      setFormTargetSports(updated.length === 0 ? ['all'] : updated);
    } else {
      setFormTargetSports([...current, sport]);
    }
  };

  const togglePlacementInForm = (location: SponsorPlacementLocation) => {
    if (formPlacementLocations.includes(location)) {
      if (formPlacementLocations.length > 1) {
        setFormPlacementLocations(formPlacementLocations.filter((l) => l !== location));
      }
    } else {
      setFormPlacementLocations([...formPlacementLocations, location]);
    }
  };

  const categoryLabelMap: Record<string, string> = {
    gear: 'Gear & Equipment',
    apparel: 'Apparel & Kicks',
    beverage: 'Hydration & Fuel',
    scouting: 'Scouting Agency',
    tournament: 'Tournament Partner',
    nutrition: 'Nutrition & Recovery',
    tech: 'Wearable Recovery Tech',
    facility: 'Facility & Court Partner',
  };

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="p-6 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 rounded-[2.5rem] border border-lime-400/40 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center space-x-2">
            <span className="px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black italic uppercase rounded-full flex items-center space-x-1">
              <Megaphone className="w-3.5 h-3.5" />
              <span>SPONSOR CONTROL & ADVERTISING ENGINE</span>
            </span>
            <span className="text-xs text-indigo-300 font-mono">PRO MODULE</span>
          </div>

          <h2 className="text-2xl sm:text-3xl font-black italic uppercase text-white tracking-tight">
            Sports Industry Sponsor Management
          </h2>
          <p className="text-xs sm:text-sm text-indigo-200/90 leading-relaxed font-medium">
            Manage non-intrusive scouting agency showcases, footwear brand partnerships, hydration sponsors, and tournament purse partners rendered across player dashboards, brackets, and feeds.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto">
          {onResetSponsors && (
            <button
              type="button"
              onClick={() => {
                triggerHaptic('medium');
                onResetSponsors();
              }}
              className="px-4 py-3 bg-indigo-900/80 hover:bg-indigo-800 text-indigo-200 border border-white/15 font-black italic uppercase text-xs rounded-2xl transition flex items-center justify-center space-x-2 cursor-pointer"
            >
              <RefreshCw className="w-4 h-4 text-lime-400" />
              <span>Reset Demo Sponsors</span>
            </button>
          )}

          <button
            type="button"
            onClick={openCreateModal}
            className="px-5 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-2xl shadow-xl shadow-lime-400/20 transition flex items-center justify-center space-x-2 cursor-pointer"
          >
            <Plus className="w-4 h-4 stroke-[3]" />
            <span>Add New Sponsor Campaign</span>
          </button>
        </div>
      </div>

      {/* Analytics KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
          <div className="flex items-center justify-between text-indigo-300 text-[11px] font-bold uppercase">
            <span>Active Campaigns</span>
            <Megaphone className="w-4 h-4 text-lime-400" />
          </div>
          <div className="text-2xl font-black text-white">{totalActive} <span className="text-xs text-indigo-300 font-normal">/ {sponsors.length}</span></div>
          <p className="text-[10px] text-lime-400/90 font-medium">Live on player dashboards & feeds</p>
        </div>

        <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
          <div className="flex items-center justify-between text-indigo-300 text-[11px] font-bold uppercase">
            <span>Network Impressions</span>
            <Eye className="w-4 h-4 text-indigo-300" />
          </div>
          <div className="text-2xl font-black text-white">{totalImpressions.toLocaleString()}</div>
          <p className="text-[10px] text-indigo-300 font-medium">Non-intrusive ad card views</p>
        </div>

        <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
          <div className="flex items-center justify-between text-indigo-300 text-[11px] font-bold uppercase">
            <span>Clicks & Engagement</span>
            <MousePointerClick className="w-4 h-4 text-amber-400" />
          </div>
          <div className="text-2xl font-black text-amber-300">{totalClicks.toLocaleString()} <span className="text-xs text-indigo-300">({avgCtr}% CTR)</span></div>
          <p className="text-[10px] text-amber-300/80 font-medium">Direct offer & promo clicks</p>
        </div>

        <div className="p-4 bg-indigo-950/80 rounded-2xl border border-white/10 space-y-1">
          <div className="flex items-center justify-between text-indigo-300 text-[11px] font-bold uppercase">
            <span>XP Distributed</span>
            <Award className="w-4 h-4 text-lime-400" />
          </div>
          <div className="text-2xl font-black text-lime-400">+{totalXpClaimed.toLocaleString()} XP</div>
          <p className="text-[10px] text-lime-300/80 font-medium">Sponsor engagement athlete rewards</p>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="p-4 bg-indigo-950/90 rounded-2xl border border-white/10 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          {/* Search Box */}
          <div className="relative">
            <Search className="w-4 h-4 text-indigo-300 absolute left-3 top-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by brand, promo code..."
              className="w-full pl-9 pr-3 py-2 bg-indigo-900/60 border border-white/15 rounded-xl text-xs text-white placeholder-indigo-300/60 focus:outline-none focus:border-lime-400 transition"
            />
          </div>

          {/* Category Dropdown */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-full px-3 py-2 bg-indigo-900/60 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
          >
            <option value="all">All Categories</option>
            <option value="gear">Gear & Equipment</option>
            <option value="apparel">Apparel & Kicks</option>
            <option value="beverage">Hydration & Fuel</option>
            <option value="scouting">Scouting Agency</option>
            <option value="tournament">Tournament Partner</option>
            <option value="nutrition">Nutrition & Recovery</option>
            <option value="tech">Wearable Recovery Tech</option>
          </select>

          {/* Status Dropdown */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full px-3 py-2 bg-indigo-900/60 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active Only</option>
            <option value="paused">Paused Only</option>
            <option value="draft">Draft Only</option>
          </select>

          {/* Placement Dropdown */}
          <select
            value={placementFilter}
            onChange={(e) => setPlacementFilter(e.target.value)}
            className="w-full px-3 py-2 bg-indigo-900/60 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
          >
            <option value="all">All Placements</option>
            <option value="tournaments">Tournaments & Brackets</option>
            <option value="scouts">Scout Directory</option>
            <option value="dashboard">Athlete Dashboard</option>
            <option value="leaderboard">Leaderboards</option>
            <option value="social_feed">Social Feed</option>
          </select>
        </div>
      </div>

      {/* Sponsor Cards Grid */}
      {filteredSponsors.length === 0 ? (
        <div className="p-12 text-center bg-indigo-950/60 rounded-[2.5rem] border border-white/10 space-y-4">
          <Megaphone className="w-12 h-12 text-indigo-400 mx-auto opacity-50" />
          <div className="space-y-1">
            <h3 className="text-base font-black italic uppercase text-white">No Sponsor Campaigns Found</h3>
            <p className="text-xs text-indigo-200/70 max-w-sm mx-auto">
              No sponsor partners match your current filter parameters. Try clearing search filters or add a new sponsor campaign.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="px-4 py-2 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow transition"
          >
            + Create Sponsor Campaign
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredSponsors.map((sponsor) => {
            const ctr = sponsor.impressionCount > 0 
              ? ((sponsor.clickCount / sponsor.impressionCount) * 100).toFixed(2) 
              : '0.00';

            return (
              <div 
                key={sponsor.id} 
                className={`p-6 rounded-[2rem] border transition space-y-4 relative overflow-hidden shadow-xl ${
                  sponsor.status === 'active' 
                    ? 'bg-indigo-950/90 border-lime-400/40' 
                    : 'bg-indigo-950/50 border-white/10 opacity-75'
                }`}
              >
                {/* Sponsor Top Bar */}
                <div className="flex items-start justify-between gap-3 pb-3 border-b border-white/10">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-12 h-12 rounded-xl bg-black border border-white/20 p-1.5 shadow flex-shrink-0 flex items-center justify-center">
                      <img 
                        src={sponsor.logoUrl} 
                        alt={sponsor.brandName} 
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-base font-black italic uppercase text-white truncate">{sponsor.brandName}</h3>
                        <span className={`px-2 py-0.5 text-[9px] font-black italic uppercase rounded-full border ${
                          sponsor.sponsorTier === 'title' 
                            ? 'bg-amber-400/20 text-amber-300 border-amber-400/40' 
                            : 'bg-lime-400/20 text-lime-300 border-lime-400/40'
                        }`}>
                          {sponsor.sponsorTier.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-[11px] text-indigo-300/80 font-medium truncate">{sponsor.companyName}</p>
                    </div>
                  </div>

                  {/* Status Switch */}
                  <button
                    type="button"
                    onClick={() => {
                      triggerHaptic('light');
                      onUpdateSponsor(sponsor.id, {
                        status: sponsor.status === 'active' ? 'paused' : 'active'
                      });
                    }}
                    className={`px-3 py-1 rounded-full text-[10px] font-black italic uppercase border transition flex items-center space-x-1 ${
                      sponsor.status === 'active'
                        ? 'bg-lime-400/20 text-lime-300 border-lime-400/40 hover:bg-lime-400/30'
                        : 'bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${sponsor.status === 'active' ? 'bg-lime-400 animate-pulse' : 'bg-slate-500'}`} />
                    <span>{sponsor.status}</span>
                  </button>
                </div>

                {/* Campaign Headline & Copy */}
                <div className="space-y-1.5">
                  <h4 className="text-sm font-black italic uppercase text-lime-300 tracking-tight leading-tight">
                    {sponsor.headline}
                  </h4>
                  <p className="text-xs text-indigo-200/90 leading-relaxed font-medium">
                    {sponsor.description}
                  </p>
                </div>

                {/* Placements & Sports Chips */}
                <div className="space-y-2 pt-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-bold text-indigo-300/70 uppercase">Placements:</span>
                    {sponsor.placementLocations.map((loc) => (
                      <span key={loc} className="px-2 py-0.5 bg-indigo-900/80 text-indigo-200 text-[9px] font-extrabold uppercase rounded-lg border border-white/10">
                        {loc.replace('_', ' ')}
                      </span>
                    ))}
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[10px] font-bold text-indigo-300/70 uppercase">Sports:</span>
                    {sponsor.targetSports.map((sp) => (
                      <span key={sp} className="px-2 py-0.5 bg-lime-400/10 text-lime-300 text-[9px] font-bold uppercase rounded-lg border border-lime-400/20">
                        {sp}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Performance Stat Row */}
                <div className="p-3 bg-indigo-900/60 rounded-xl border border-white/10 grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-[10px] text-indigo-300 font-bold uppercase">Impressions</div>
                    <div className="text-xs font-black text-white">{sponsor.impressionCount.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-indigo-300 font-bold uppercase">Clicks</div>
                    <div className="text-xs font-black text-amber-300">{sponsor.clickCount.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-indigo-300 font-bold uppercase">CTR %</div>
                    <div className="text-xs font-black text-lime-400">{ctr}%</div>
                  </div>
                </div>

                {/* Bottom Actions Bar */}
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  {sponsor.discountCode ? (
                    <div className="flex items-center space-x-1.5 text-xs text-indigo-200">
                      <Tag className="w-3.5 h-3.5 text-lime-400" />
                      <span>Code: <strong className="text-white font-mono">{sponsor.discountCode}</strong></span>
                    </div>
                  ) : <div />}

                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      onClick={() => openEditModal(sponsor)}
                      className="px-3 py-1.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 rounded-xl text-xs font-bold border border-white/15 transition flex items-center space-x-1 cursor-pointer"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                      <span>Edit</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        triggerHaptic('medium');
                        setDeleteConfirmId(sponsor.id);
                      }}
                      className="p-1.5 bg-rose-950/80 hover:bg-rose-900 text-rose-300 rounded-xl border border-rose-500/30 transition cursor-pointer"
                      title="Delete Sponsor Campaign"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create / Edit Sponsor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
          <div className="max-w-2xl w-full bg-indigo-950 border-2 border-lime-400/50 rounded-[2.5rem] p-6 sm:p-8 space-y-6 shadow-2xl relative my-8">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="absolute top-6 right-6 p-2 rounded-full bg-indigo-900/80 text-indigo-300 hover:text-white transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="space-y-1">
              <div className="inline-block px-3 py-1 bg-lime-400/20 text-lime-300 border border-lime-400/40 text-[10px] font-black italic uppercase rounded-full">
                ADMIN CONTROL PANEL
              </div>
              <h3 className="text-2xl font-black italic uppercase text-white">
                {editingSponsor ? 'Edit Sponsor Campaign' : 'Add New Sponsor Partner'}
              </h3>
              <p className="text-xs text-indigo-200/80">
                Configure brand details, CTA offer links, promo codes, and placement locations.
              </p>
            </div>

            <form onSubmit={handleSaveSponsor} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Brand Name */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Brand Display Name *</label>
                  <input
                    type="text"
                    required
                    value={formBrandName}
                    onChange={(e) => setFormBrandName(e.target.value)}
                    placeholder="e.g. Nike Basketball"
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                </div>

                {/* Company Name */}
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Company Legal Name</label>
                  <input
                    type="text"
                    value={formCompanyName}
                    onChange={(e) => setFormCompanyName(e.target.value)}
                    placeholder="e.g. Nike Inc."
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                </div>
              </div>

              {/* Logo URL & Presets */}
              <div className="space-y-2">
                <label className="text-[11px] font-bold uppercase text-indigo-300">Sponsor Logo Image URL *</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    required
                    value={formLogoUrl}
                    onChange={(e) => setFormLogoUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                  {formLogoUrl && (
                    <div className="w-10 h-10 rounded-xl bg-black border border-white/20 p-1 flex-shrink-0 flex items-center justify-center">
                      <img src={formLogoUrl} alt="Logo Preview" className="w-full h-full object-contain" />
                    </div>
                  )}
                </div>

                {/* Quick Presets */}
                <div className="flex items-center space-x-2 pt-1">
                  <span className="text-[10px] text-indigo-300/70 font-bold uppercase">Presets:</span>
                  <div className="flex flex-wrap gap-1.5">
                    {PRESET_LOGOS.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => {
                          setFormLogoUrl(p.url);
                          setFormBrandName(p.name);
                        }}
                        className="px-2 py-1 bg-indigo-900/90 hover:bg-indigo-800 text-indigo-200 text-[10px] font-bold rounded-lg border border-white/10 transition cursor-pointer"
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Category & Tier */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Category *</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as SponsorCategory)}
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  >
                    <option value="gear">Gear & Equipment</option>
                    <option value="apparel">Apparel & Kicks</option>
                    <option value="beverage">Hydration & Fuel</option>
                    <option value="scouting">Scouting Agency</option>
                    <option value="tournament">Tournament Partner</option>
                    <option value="nutrition">Nutrition & Recovery</option>
                    <option value="tech">Wearable Recovery Tech</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Sponsor Tier *</label>
                  <select
                    value={formTier}
                    onChange={(e) => setFormTier(e.target.value as SponsorTier)}
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  >
                    <option value="title">Title Sponsor (Top Banner Placement)</option>
                    <option value="gold">Gold Partner</option>
                    <option value="silver">Silver Partner</option>
                    <option value="partner">Official Partner</option>
                  </select>
                </div>
              </div>

              {/* Placement Locations (Checkboxes) */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-bold uppercase text-indigo-300">App Placement Locations *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    { id: 'tournaments', label: 'Tournaments & Brackets' },
                    { id: 'scouts', label: 'Scout Directory' },
                    { id: 'dashboard', label: 'Athlete Dashboard' },
                    { id: 'leaderboard', label: 'Leaderboard Rankings' },
                    { id: 'social_feed', label: 'Social Community Feed' },
                  ].map((loc) => {
                    const isChecked = formPlacementLocations.includes(loc.id as SponsorPlacementLocation);
                    return (
                      <button
                        key={loc.id}
                        type="button"
                        onClick={() => togglePlacementInForm(loc.id as SponsorPlacementLocation)}
                        className={`p-2.5 rounded-xl text-xs font-bold border transition text-left flex items-center justify-between cursor-pointer ${
                          isChecked 
                            ? 'bg-lime-400/20 text-lime-300 border-lime-400/50' 
                            : 'bg-indigo-900/50 text-indigo-300 border-white/10'
                        }`}
                      >
                        <span>{loc.label}</span>
                        {isChecked && <Check className="w-3.5 h-3.5 text-lime-400" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Headline & Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-indigo-300">Campaign Headline *</label>
                <input
                  type="text"
                  required
                  value={formHeadline}
                  onChange={(e) => setFormHeadline(e.target.value)}
                  placeholder="e.g. Official Court Footwear & Pro Series Partner"
                  className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                />
              </div>

              <div className="space-y-1">
                <label className="text-[11px] font-bold uppercase text-indigo-300">Description & Offer Copy</label>
                <textarea
                  rows={2}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Describe the offer or partner perks for players..."
                  className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition resize-none"
                />
              </div>

              {/* CTA Label, Link & Promo Code */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">CTA Button Text</label>
                  <input
                    type="text"
                    value={formCtaText}
                    onChange={(e) => setFormCtaText(e.target.value)}
                    placeholder="e.g. Claim Nike Perk"
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">CTA Destination URL</label>
                  <input
                    type="text"
                    value={formCtaLink}
                    onChange={(e) => setFormCtaLink(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Discount Code</label>
                  <input
                    type="text"
                    value={formDiscountCode}
                    onChange={(e) => setFormDiscountCode(e.target.value.toUpperCase())}
                    placeholder="e.g. PLAYGROUND20"
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white font-mono uppercase focus:outline-none focus:border-lime-400 transition"
                  />
                </div>
              </div>

              {/* XP Reward & Status */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">XP Engagement Bonus (+XP)</label>
                  <input
                    type="number"
                    min={0}
                    max={500}
                    value={formXpReward}
                    onChange={(e) => setFormXpReward(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] font-bold uppercase text-indigo-300">Status</label>
                  <select
                    value={formStatus}
                    onChange={(e) => setFormStatus(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 bg-indigo-900/70 border border-white/15 rounded-xl text-xs text-white focus:outline-none focus:border-lime-400 transition"
                  >
                    <option value="active">Active (Visible in App)</option>
                    <option value="paused">Paused</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              {/* Live Preview Box */}
              <div className="p-4 bg-indigo-950 rounded-2xl border border-lime-400/40 space-y-2">
                <div className="flex items-center space-x-1 text-[10px] font-black uppercase text-lime-400">
                  <Sparkles className="w-3 h-3" />
                  <span>Real-Time Ad Card Preview in App</span>
                </div>

                <div className="p-3 bg-indigo-900/90 rounded-xl border border-white/10 flex items-center justify-between space-x-3">
                  <div className="flex items-center space-x-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-black border border-white/20 p-1 flex-shrink-0 flex items-center justify-center">
                      <img src={formLogoUrl || PRESET_LOGOS[0].url} alt="Logo" className="w-full h-full object-contain" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-[9px] font-bold text-lime-400 uppercase">{formBrandName || 'Brand Name'}</span>
                      <h5 className="text-xs font-black text-white truncate">{formHeadline || 'Campaign Headline'}</h5>
                      <p className="text-[10px] text-indigo-200/80 truncate">{formDescription}</p>
                    </div>
                  </div>
                  <button type="button" className="px-3 py-1 bg-lime-400 text-black text-[10px] font-black uppercase rounded-lg">
                    {formCtaText}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-5 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 font-bold text-xs rounded-xl transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase text-xs rounded-xl shadow-lg shadow-lime-400/20 transition cursor-pointer"
                >
                  {editingSponsor ? 'Save Changes' : 'Publish Sponsor Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-indigo-950 border-2 border-rose-500/50 rounded-3xl p-6 space-y-4 shadow-2xl text-center">
            <Trash2 className="w-12 h-12 text-rose-400 mx-auto" />
            <div className="space-y-1">
              <h3 className="text-lg font-black italic uppercase text-white">Delete Sponsor Campaign?</h3>
              <p className="text-xs text-indigo-200/80">
                Are you sure you want to delete this sponsor? This will remove its ad placement across all view streams.
              </p>
            </div>

            <div className="flex items-center space-x-3 pt-2">
              <button
                type="button"
                onClick={() => setDeleteConfirmId(null)}
                className="flex-1 py-2.5 bg-indigo-900 hover:bg-indigo-800 text-indigo-200 text-xs font-bold rounded-xl transition"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={() => {
                  triggerHaptic('medium');
                  onDeleteSponsor(deleteConfirmId);
                  setDeleteConfirmId(null);
                }}
                className="flex-1 py-2.5 bg-rose-600 hover:bg-rose-500 text-white font-black italic uppercase text-xs rounded-xl shadow-lg transition"
              >
                Delete Sponsor
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
